<!-- Copyright: Seán I. O'Donoghue -->
<!-- Back 10 s, play/pause and forward 10 s over the Vimeo player, as on the iPhone's player
     (Seán, 2026-10-21: icons and spacing from YouTube on an iPhone): an overlay above the iframe,
     Vimeo's own controls untouched. They come and go with Vimeo's control bar: its player
     reports it (controlbarvisibilitychanged, in its API's events though not in its docs), on a
     mouse move or a tap; they also stay while the pointer or the keyboard's focus is on them -->
<template>
  <div
    class="skip-controls"
    :class="{isVisible}"
    @focusin="onFocusIn"
    @focusout="isKeyboardFocus = false"
    @pointerover="isPointerOver = $event.pointerType === 'mouse'"
    @pointerout="isPointerOver = false">
    <button
      type="button"
      class="skip back"
      aria-label="Back 10 seconds"
      @click="onButtonClick($event, () => skip(-10))">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path class="arc" :d="ARC" />
        <path class="head" :d="HEAD" />
        <text x="12" y="15.7">10</text>
      </svg>
    </button>
    <button
      type="button"
      class="playPause"
      :aria-label="isPaused ? 'Play' : 'Pause'"
      @click="onButtonClick($event, togglePlay)">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path v-if="isPaused" :d="PLAY" />
        <g v-else>
          <rect x="6.6" y="5" width="3.8" height="14" rx="1.2" />
          <rect x="13.6" y="5" width="3.8" height="14" rx="1.2" />
        </g>
      </svg>
    </button>
    <button
      type="button"
      class="skip forward"
      aria-label="Forward 10 seconds"
      @click="onButtonClick($event, () => skip(10))">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <g class="isMirrored">
          <path class="arc" :d="ARC" />
          <path class="head" :d="HEAD" />
        </g>
        <text x="12" y="15.7">10</text>
      </svg>
    </button>
    <button
      v-if="canFullscreen"
      type="button"
      class="fullscreen"
      :aria-label="isFullscreen ? 'Exit full screen' : 'Full screen'"
      @click="onButtonClick($event, toggleFullscreen)">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path class="corners" :d="isFullscreen ? EXIT_CORNERS : CORNERS" />
      </svg>
    </button>
  </div>
</template>

<script setup>
const props = defineProps({
  player: {type: Object, default: null}, // the Vimeo SDK's Player
  container: {type: Object, default: null} // the element around the iframe
});
import {computed, onBeforeUnmount, ref, watch} from "vue";
import {getCanFullscreen, useOverlayFullscreen} from "@/components/Video/useOverlayFullscreen";

// the iPhone's arrow: a circle open at about eleven o'clock, the head at the top pointing back
const ARC = "M12.6 3.7 A8.3 8.3 0 1 1 5.2 7.6";
const HEAD = "M13.2 1.3 L9.6 3.7 L13.2 6.1 Z";
const PLAY = "M8.5 5.6 Q8.5 4.4 9.6 5 L19 10.9 Q20 11.5 19 12.1 L9.6 18 Q8.5 18.6 8.5 17.4 Z";
const CORNERS = "M4 9V4h5 M15 4h5v5 M20 15v5h-5 M9 20H4v-5";
const EXIT_CORNERS = "M9 4v5H4 M20 9h-5V4 M15 20v-5h5 M4 15h5v5";
const PLAYER_EVENTS = ["play", "pause"];
const BAR_EVENT = "controlbarvisibilitychanged";

function getClamped(seconds, duration) {
  return Math.min(Math.max(seconds, 0), Math.max(duration - 0.5, 0));
}

const canFullscreen = getCanFullscreen();
const {isFullscreen, toggleFullscreen} = useOverlayFullscreen(() => props.container);
const isBarVisible = ref(false);
const isPointerOver = ref(false);
const isKeyboardFocus = ref(false);
const isPaused = ref(false);
const isVisible = computed(() => {
  return isBarVisible.value || isPointerOver.value || isKeyboardFocus.value;
});

async function skip(seconds) {
  const player = props.player;
  if (!player) return;
  const [current, duration] = await Promise.all([player.getCurrentTime(), player.getDuration()]);
  await player.setCurrentTime(getClamped(current + seconds, duration)).catch(() => {});
}

async function togglePlay() {
  const player = props.player;
  if (!player) return;
  if (await player.getPaused()) return player.play().catch(() => {});
  player.pause().catch(() => {});
}

// a tap or click lets go of the button, so the controls fade with Vimeo's; a key (Enter, Space:
// detail 0) keeps it, and its focus keeps them shown
function onButtonClick(event, action) {
  if (event.detail) event.currentTarget.blur();
  action();
}

// a button reached by Tab keeps them shown
function onFocusIn(event) {
  isKeyboardFocus.value = event.target.matches(":focus-visible");
}

const handlers = {
  play: () => (isPaused.value = false),
  pause: () => (isPaused.value = true),
  [BAR_EVENT]: (data) => (isBarVisible.value = Boolean(data?.visible))
};

function setPlayerListeners(player, method) {
  for (const name of [...PLAYER_EVENTS, BAR_EVENT]) player?.[method](name, handlers[name]);
}

watch(() => props.player, (player, oldPlayer) => {
  setPlayerListeners(oldPlayer, "off");
  setPlayerListeners(player, "on");
  player?.getPaused().then((paused) => (isPaused.value = paused)).catch(() => {});
}, {immediate: true});
onBeforeUnmount(() => setPlayerListeners(props.player, "off"));
</script>

<style scoped>
/* sizes from the iPhone's player, as shares of the video's width (cqw): play/pause 12%, the
   skip buttons 8.3%, their centres 19% either side of the middle; never under a finger's 44 px.
   Between and around them, taps reach Vimeo's player */
.skip-controls {
  position: absolute;
  inset: 0;
  z-index: 5;
  container-type: size;
  pointer-events: none;
  outline: none;
  --play-size: clamp(60px, 12cqw, 120px);
  --skip-size: clamp(46px, 8.3cqw, 84px);
  --skip-offset: max(19cqw, calc(var(--play-size) / 2 + var(--skip-size) / 2 + 14px));
}
button {
  position: absolute;
  top: 50%;
  left: 50%;
  width: var(--skip-size);
  height: var(--skip-size);
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  /* the iPhone's glass: the video blurred through a smoky tint, a light rim */
  background: rgba(40, 40, 40, 0.28);
  -webkit-backdrop-filter: blur(14px) saturate(1.5);
  backdrop-filter: blur(14px) saturate(1.5);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;
  box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.25), 0 2px 10px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  opacity: 0;
  transform: translate(-50%, -50%);
  transition: opacity 0.25s ease;
}
button.playPause {
  width: var(--play-size);
  height: var(--play-size);
}
button.back {
  left: calc(50% - var(--skip-offset));
}
button.forward {
  left: calc(50% + var(--skip-offset));
}
/* in place of Vimeo's own (hidden): the top-left corner, which Vimeo leaves free */
button.fullscreen {
  top: 12px;
  left: 12px;
  width: 40px;
  height: 40px;
  transform: none;
}
.isVisible button {
  opacity: 1;
  pointer-events: auto;
}
button:focus-visible {
  outline: 3px solid #fff;
  outline-offset: 2px;
}
button:active {
  background: rgba(40, 40, 40, 0.5);
}
svg {
  width: 52%;
  height: 52%;
  fill: currentColor;
}
.playPause svg {
  width: 44%;
  height: 44%;
}
.isMirrored {
  transform: scaleX(-1);
  transform-origin: 12px 12px;
}
.corners {
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.arc {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
}
text {
  font-size: 8.4px;
  font-weight: 600;
  text-anchor: middle;
  font-family: -apple-system, system-ui, sans-serif;
  letter-spacing: -0.3px;
}
</style>
